import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { FlatpickrLocaleService } from 'src/app/shared/services/flatpickr-locale.service';
import { Currency } from '../../../../setting/models/Currency';
import { CompanyService } from '../../../../setting/services/company.service';
import { InputVisibilityService } from '../../../../shared/services/InputVisibilityService';
import { RealWage } from '../../../models/RealWage';
import { SalaryService } from '../../../services/salary.service';

@Component({
    selector: 'app-create-edit-employee-real-wage-modal',
    templateUrl: './create-edit-employee-real-wage-modal.component.html',
    styleUrls: ['./create-edit-employee-real-wage-modal.component.css']
})
export class CreateEditEmployeeRealWageModalComponent implements OnInit, OnDestroy {
    public realWage: RealWage;
    public realWageEditForm: UntypedFormGroup;
    public employeeID: number;
    public locale$ = this._flatpickrLocale.currentFlatpickrLocale$;
    public currencies$: Observable<Currency[]>;
    public canEdit = false;
    public submitted = false;

    private _subscriptions = new Subscription();

    public constructor(
        public activeModal: NgbActiveModal,
        private _authService: AuthenticationService,
        private _salaryService: SalaryService,
        private _companyService: CompanyService,
        private _fb: UntypedFormBuilder,
        private _flatpickrLocale: FlatpickrLocaleService,
        private _inputVisibilityService: InputVisibilityService,
    ) { }

    public get hiddenInputs(): boolean {
        return this._inputVisibilityService.hiddenInputs;
    }

    public get f(): { [formControlName: string]: AbstractControl; } {
        return this.realWageEditForm.controls;
    }

    public ngOnDestroy(): void {
        this._subscriptions?.unsubscribe();
    }

    public ngOnInit(): void {
        this.realWageEditForm = this._fb.group({
            fix: [0, Validators.required],
            variable: [0, Validators.required],
            levy: [0],
            other: [0],
            date: [null, Validators.required],
            note: [''],
            fix_normalized: [{value: 0, disabled: true}],
            variable_normalized: [{value: 0, disabled: true}],
            levy_normalized: [{value: 0, disabled: true}],
            other_normalized: [{value: 0, disabled: true}],
            employee_currency_code: [null, Validators.required],
            company_currency_code: [{value: null, disabled: true}],
            rate: [null, Validators.required]
        });

        if (this.realWage) {
            this.realWageEditForm.patchValue(this.realWage);
        }

        if (this.hiddenInputs) {
            this.realWageEditForm.disable();
        }

        this.canEdit = this._authService.checkPermission(['employeeRealWage.edit', 'employeeRealWage.editStructure']);

        if (!this.canEdit) {
            this.realWageEditForm.disable();
        }

        this.currencies$ = this._companyService.getCurrencies()
            .pipe(
                tap(currencies => this.realWageEditForm.patchValue({company_currency_code: currencies.find(currency => currency.code === this._authService.loggedUser.currency_code).code}))
            );

        this._checkForFormChanges();
    }

    public deleteRealWage(): void {
        this._salaryService.deleteRealWageByID(this.employeeID, this.realWage.employee_real_wage_ID)
            .then(() => this.activeModal.close('deleted'),
                () => { });
    }

    public onSubmit(): void {
        this.submitted = true;

        if (!this.realWageEditForm.valid) {
            return;
        }

        const realWage = this.realWageEditForm.value;
        realWage.employee_real_wage_ID = this.realWage?.employee_real_wage_ID;

        this._salaryService.saveRealWageByEmployeeID(realWage, this.employeeID)
            .then(() => this.activeModal.close('saved'),
                () => this.activeModal.close('error'));
    }

    private _checkForFormChanges(): void {
        this._subscriptions.add(
            this.realWageEditForm.valueChanges.subscribe(value => {
                if (value.rate > 0) {
                    this.realWageEditForm.patchValue({
                        fix_normalized: value.fix / value.rate,
                        variable_normalized: value.variable / value.rate,
                        levy_normalized: value.levy / value.rate,
                        other_normalized: value.other / value.rate,
                    }, {emitEvent: false});
                }
            })
        );
    }
}
